/**
 * Global Settings Page
 * Global routing configuration for the entire platform
 */

import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Button,
    Switch,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    ChevronRight as ChevronRightIcon,
    AccountTree as AccountTreeIcon,
    AttachMoney as AttachMoneyIcon,
    Timer as TimerIcon,
} from '@mui/icons-material';
import { toast } from 'sonner';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { globalSettingsService } from '../../services/GlobalSettingsService';

// Priority type for drag and drop
type PriorityType = 'cost' | 'latency';

type SettingsSection =
    | 'routing-configuration'
    | 'performance'
    | 'security';

interface PriorityItem {
    id: PriorityType;
    label: string;
    icon: React.ReactNode;
    color: string;
    bgcolor: string;
}

// Draggable Priority Item Component
interface DraggablePriorityItemProps {
    item: PriorityItem;
    index: number;
    moveItem: (dragIndex: number, hoverIndex: number) => void;
}

const DraggablePriorityItem: React.FC<DraggablePriorityItemProps> = ({ item, index, moveItem }) => {
    const [{ isDragging }, drag] = useDrag({
        type: 'PRIORITY_ITEM',
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [, drop] = useDrop({
        accept: 'PRIORITY_ITEM',
        hover: (draggedItem: { index: number }) => {
            if (draggedItem.index !== index) {
                moveItem(draggedItem.index, index);
                draggedItem.index = index;
            }
        },
    });

    return (
        <Box
            ref={(node: HTMLDivElement | null) => {
                if (node) drag(drop(node));
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                bgcolor: item?.bgcolor,
                border: '2px solid',
                borderColor: item?.color,
                borderRadius: 2,
                cursor: 'move',
                opacity: isDragging ? 0.5 : 1,
                transition: 'all 0.2s',
                '&:hover': {
                    boxShadow: `0 4px 12px ${item?.color}40`,
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <Box
                sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 1,
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    color: item?.color,
                    fontSize: '0.875rem'
                }}
            >
                {index + 1}
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: item?.color }}>
                    {item?.label}
                </Typography>
            </Box>
        </Box>
    );
};

interface RoutingSettings {
    key: string;
    value: PriorityType[];
    is_active: boolean;
}

export function GlobalSettingsPage(routing: RoutingSettings) {
    const [activeSection, setActiveSection] = useState<SettingsSection>('routing-configuration');
    const [loading, setLoading] = useState<boolean>(false);
    const [routingSettings, setRoutingSettings] = useState<RoutingSettings>(routing);

    const menuItems = [
        { id: 'routing-configuration', label: 'Routing Configuration', icon: <AccountTreeIcon fontSize="small" /> }
    ];

    const getPriorityItems = (order: PriorityType[]): PriorityItem[] => {
        const itemMap: Record<PriorityType, PriorityItem> = {
            cost: {
                id: 'cost',
                label: 'Cost',
                icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />,
                color: '#1976d2',
                bgcolor: '#e3f2fd'
            },
            latency: {
                id: 'latency',
                label: 'Latency',
                icon: <TimerIcon sx={{ fontSize: 20 }} />,
                color: '#1976d2',
                bgcolor: '#e3f2fd'
            }
        };
        const result = order.map(id => itemMap[id]);
        return result;
    };

    const moveItem = (dragIndex: number, hoverIndex: number) => {
        const newOrder = [...routingSettings.value];
        const [removed] = newOrder.splice(dragIndex, 1);
        newOrder.splice(hoverIndex, 0, removed);
        setRoutingSettings({
            ...routingSettings,
            value: newOrder
        });
    };

    const handleSaveRoutingSettings = async () => {
        try {
            setLoading(true)
            await globalSettingsService.update(routingSettings);
            toast.success('Global routing settings saved successfully');
            setLoading(false)

        } catch (error) {
            setLoading(false)
            toast.error('Failed to save global routing settings');
        }
    };

    return (
        <>
            {
                loading ? (<CircularProgress />) : (

                    <DndProvider backend={HTML5Backend}>
                        <Box sx={{ display: 'flex', minHeight: '100vh', }}>

                            {/* Main Content Area */}
                            <Box sx={{ flex: 1, p: 0 }}>                                

                                {/* Content */}
                                <Box>
                                    {/* <Paper sx={{ p: 3 }}> */}
                                        {activeSection === 'routing-configuration' && (
                                            <Stack spacing={3}>
                                                {/* Global Routing Header with Routing Status */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                                                            Global Routing Configuration
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Set default routing behavior for all deployments. These settings can be overridden per deployment.
                                                        </Typography>
                                                    </Box>

                                                    {/* Routing Status on the right */}
                                                    <Box sx={{ flexShrink: 0 }}>
                                                        <Switch
                                                            checked={routingSettings.is_active}
                                                            onChange={(e) => setRoutingSettings({ ...routingSettings, is_active: e.target.checked })}
                                                        />
                                                    </Box>
                                                </Box>

                                                <Divider />

                                                {/* Default Priority Configuration */}
                                                <Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                                                        Default Priority Configuration
                                                    </Typography>

                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                        Drag and drop to reorder priorities. This will be the default for new routing rules.
                                                    </Typography>

                                                    {/* Drag and Drop Priority List */}
                                                    <Stack spacing={2} sx={{ maxWidth: 400 }}>
                                                        {routingSettings.value && getPriorityItems(routingSettings.value).map((item, index) => (
                                                            <DraggablePriorityItem
                                                                key={index}
                                                                item={item}
                                                                index={index}
                                                                moveItem={moveItem}
                                                            />
                                                        ))}
                                                    </Stack>

                                                    {/* Summary Alert */}
                                                    <Alert
                                                        severity="info"
                                                        sx={{ borderRadius: 2, mt: 3 }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            Current Default Priority: {routingSettings.value && routingSettings.value.map((p: any, i: any) =>
                                                                `${p.charAt(0).toUpperCase() + p.slice(1)}=${i + 1}`
                                                            ).join(', ')}
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            New routing rules will use this priority configuration by default.
                                                        </Typography>
                                                    </Alert>
                                                </Box>
                                            </Stack>
                                        )}

                                        {activeSection !== 'routing-configuration' && (
                                            <Box sx={{ py: 8, textAlign: 'center' }}>
                                                <Typography variant="h6" color="text.secondary">
                                                    {menuItems.find(item => item.id === activeSection)?.label} Settings
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    Settings for this section will be available soon
                                                </Typography>
                                            </Box>
                                        )}
                                    {/* </Paper> */}
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        p: 3,
                                        bgcolor: '#fff',
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        onClick={handleSaveRoutingSettings}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: 3
                                        }}
                                    >
                                        Save Changes
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </DndProvider>
                )}
        </>
    );
}

export default GlobalSettingsPage;